import { ref, type Ref } from 'vue'

import {
  resolveClient,
  type ApolloClientLike,
} from '@/shared/ports/resolveClient'
import {
  AddGuildNotificationTimerDocument,
  GuildNotificationTimersDocument,
  GuildNotificationTimerStatusChannelDocument,
  ModifyGuildNotificationTimerDocument,
  RemoveGuildNotificationTimerDocument,
  SetGuildNotificationTimerStatusChannelDocument,
  StartGuildNotificationTimerDocument,
  StopGuildNotificationTimerDocument,
} from '../lib/handWrittenGraphql'
import type {
  AddGuildNotificationTimerMutation,
  GuildNotificationTimersQuery,
  GuildNotificationTimerStatusChannelQuery,
  GuildNotificationTimerStatusChannelWire,
  GuildNotificationTimerListWire,
  GuildNotificationTimerWire,
  ModifyGuildNotificationTimerMutation,
  RemoveGuildNotificationTimerMutation,
  SetGuildNotificationTimerStatusChannelMutation,
  StartGuildNotificationTimerMutation,
  StopGuildNotificationTimerMutation,
} from '../lib/handWrittenGraphql'

export type GuildNotificationTimerSource = 'manual' | 'external'
export type GuildNotificationTimerStatus =
  | 'scheduled'
  | 'running'
  | 'finished'
  | 'stopped'

type MutationTarget = 'timers' | 'statusChannel'

export interface GuildNotificationTimer {
  readonly id: string
  readonly name: string
  readonly source: GuildNotificationTimerSource
  readonly startsAt: string
  readonly endsAt: string
  readonly durationSeconds: number
  readonly active: boolean
  readonly cancelledAt: string | null
  readonly status: GuildNotificationTimerStatus
  readonly canModify: boolean
}

export interface GuildNotificationTimerStatusChannel {
  readonly guildId: string
  readonly channelId: string | null
  readonly messageId: string | null
}

export interface ModifyGuildNotificationTimerInput {
  readonly name: string | null
  readonly gameTimer: string | null
}

export interface NotificationTimersApi {
  readonly timers: Ref<ReadonlyArray<GuildNotificationTimer>>
  readonly statusChannel: Ref<GuildNotificationTimerStatusChannel | null>
  readonly loading: Ref<boolean>
  readonly error: Ref<string | null>
  readonly load: (guildId: string) => Promise<void>
  readonly add: (
    guildId: string,
    name: string | null,
    gameTimer: string | null,
  ) => Promise<boolean>
  readonly modify: (
    guildId: string,
    timerId: string,
    input: ModifyGuildNotificationTimerInput,
  ) => Promise<boolean>
  readonly start: (guildId: string, timerId: string, gameTimer: string) => Promise<boolean>
  readonly stop: (guildId: string, timerId: string) => Promise<boolean>
  readonly remove: (guildId: string, timerId: string) => Promise<boolean>
  readonly setStatusChannel: (guildId: string, channelId: string | null) => Promise<boolean>
  readonly prepareGuild: (guildId: string) => void
  readonly applyLiveState: (
    timerList: GuildNotificationTimerListWire,
    statusChannel: GuildNotificationTimerStatusChannelWire,
  ) => void
  readonly markUnavailable: (guildId: string) => void
  readonly reportLiveError: (message: string) => void
}

function timerSource(value: string): GuildNotificationTimerSource {
  if (value === 'manual' || value === 'external') return value
  throw new Error(`wwm-notify returned unsupported timer source "${value}".`)
}

function timerStatus(value: string): GuildNotificationTimerStatus {
  if (
    value === 'scheduled'
    || value === 'running'
    || value === 'finished'
    || value === 'stopped'
  ) return value
  throw new Error(`wwm-notify returned unsupported timer status "${value}".`)
}

function mapTimer(value: GuildNotificationTimerWire): GuildNotificationTimer {
  return {
    id: value.id,
    name: value.name,
    source: timerSource(value.source),
    startsAt: value.startsAt,
    endsAt: value.endsAt,
    durationSeconds: value.durationSeconds,
    active: value.active,
    cancelledAt: value.cancelledAt,
    status: timerStatus(value.status),
    canModify: value.canModify,
  }
}

function mapStatusChannel(
  value: GuildNotificationTimerStatusChannelWire,
): GuildNotificationTimerStatusChannel {
  return {
    guildId: value.guildId,
    channelId: value.channelId,
    messageId: value.messageId,
  }
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause)
}

export function useNotificationTimers(
  apolloClient: ApolloClientLike = resolveClient(),
): NotificationTimersApi {
  const timers = ref<ReadonlyArray<GuildNotificationTimer>>([])
  const statusChannel = ref<GuildNotificationTimerStatusChannel | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  let activeGuildId: string | null = null
  let generation = 0
  let mutationRevision = 0
  let timerMutationRevision = 0
  let statusChannelMutationRevision = 0
  let liveRevision = 0

  function accepts(guildId: string, requestGeneration: number): boolean {
    return activeGuildId === guildId && generation === requestGeneration
  }

  function advanceMutationRevision(
    guildId: string,
    target: MutationTarget,
  ): void {
    if (activeGuildId !== guildId) return
    mutationRevision += 1
    if (target === 'timers') timerMutationRevision += 1
    else statusChannelMutationRevision += 1
  }

  function finishMutation(guildId: string, target: MutationTarget, requestGeneration: number): void {
    if (accepts(guildId, requestGeneration)) advanceMutationRevision(guildId, target)
  }

  function requireGuild(expectedGuildId: string, receivedGuildId: string): void {
    if (expectedGuildId !== receivedGuildId) {
      throw new Error('wwm-notify returned timer data for a different guild.')
    }
  }

  function replaceTimer(nextTimer: GuildNotificationTimer): void {
    const existingIndex = timers.value.findIndex((timer) => timer.id === nextTimer.id)
    timers.value = existingIndex === -1
      ? [...timers.value, nextTimer]
      : timers.value.map((timer) => timer.id === nextTimer.id ? nextTimer : timer)
  }

  function prepareGuild(guildId: string): void {
    const guildChanged = activeGuildId !== guildId
    generation += 1
    liveRevision += 1
    activeGuildId = guildId
    loading.value = guildId !== '0'
    error.value = null
    if (guildChanged || guildId === '0') {
      timers.value = []
      statusChannel.value = null
    }
  }

  function applyLiveState(
    timerList: GuildNotificationTimerListWire,
    channel: GuildNotificationTimerStatusChannelWire,
  ): void {
    if (activeGuildId !== timerList.guildId || activeGuildId !== channel.guildId) return
    liveRevision += 1
    timers.value = timerList.timers.map(mapTimer)
    statusChannel.value = mapStatusChannel(channel)
    loading.value = false
    error.value = null
  }

  function markUnavailable(guildId: string): void {
    if (activeGuildId !== guildId) return
    liveRevision += 1
    timers.value = []
    statusChannel.value = null
    loading.value = false
    error.value = null
  }

  function reportLiveError(message: string): void {
    loading.value = false
    error.value = message
  }

  async function load(guildId: string): Promise<void> {
    const requestGeneration = ++generation
    const requestMutationRevision = mutationRevision
    const requestTimerMutationRevision = timerMutationRevision
    const requestStatusChannelMutationRevision = statusChannelMutationRevision
    const requestLiveRevision = liveRevision
    const guildChanged = activeGuildId !== guildId
    activeGuildId = guildId
    error.value = null
    if (guildChanged) {
      timers.value = []
      statusChannel.value = null
    }
    if (guildId === '0') {
      loading.value = false
      return
    }
    loading.value = true
    try {
      const [timerResult, channelResult] = await Promise.all([
        apolloClient.query<GuildNotificationTimersQuery>({
          query: GuildNotificationTimersDocument,
          variables: { guildId },
          fetchPolicy: 'no-cache',
        }),
        apolloClient.query<GuildNotificationTimerStatusChannelQuery>({
          query: GuildNotificationTimerStatusChannelDocument,
          variables: { guildId },
          fetchPolicy: 'no-cache',
        }),
      ])
      if (!accepts(guildId, requestGeneration)) return
      if (requestLiveRevision !== liveRevision) return
      const timerList = timerResult.data?.guildNotificationTimers
      const channel = channelResult.data?.guildNotificationTimerStatusChannel
      const timersAreCurrent = timerMutationRevision === requestTimerMutationRevision
      const statusChannelIsCurrent = statusChannelMutationRevision
        === requestStatusChannelMutationRevision
      if (timersAreCurrent && (timerList === undefined || timerList === null)) {
        throw new Error('wwm-notify returned incomplete timer settings.')
      }
      if (statusChannelIsCurrent && (channel === undefined || channel === null)) {
        throw new Error('wwm-notify returned incomplete timer settings.')
      }
      if (timersAreCurrent && timerList !== undefined && timerList !== null) {
        requireGuild(guildId, timerList.guildId)
      }
      if (statusChannelIsCurrent && channel !== undefined && channel !== null) {
        requireGuild(guildId, channel.guildId)
      }
      if (timersAreCurrent && timerList !== undefined && timerList !== null) {
        timers.value = timerList.timers.map(mapTimer)
      }
      if (statusChannelIsCurrent && channel !== undefined && channel !== null) {
        statusChannel.value = mapStatusChannel(channel)
      }
    } catch (cause) {
      if (
        accepts(guildId, requestGeneration)
        && requestLiveRevision === liveRevision
        && mutationRevision === requestMutationRevision
      ) error.value = errorMessage(cause)
    } finally {
      if (accepts(guildId, requestGeneration)) loading.value = false
    }
  }

  async function add(
    guildId: string,
    name: string | null,
    gameTimer: string | null,
  ): Promise<boolean> {
    const requestGeneration = generation
    const requestLiveRevision = liveRevision
    advanceMutationRevision(guildId, 'timers')
    error.value = null
    try {
      const result = await apolloClient.mutate<AddGuildNotificationTimerMutation>({
        mutation: AddGuildNotificationTimerDocument,
        variables: { guildId, name, gameTimer },
      })
      if (!accepts(guildId, requestGeneration)) return false
      const timer = result.data?.addGuildNotificationTimer
      if (timer === undefined || timer === null) throw new Error('wwm-notify did not return the new timer.')
      if (requestLiveRevision === liveRevision) replaceTimer(mapTimer(timer))
      error.value = null
      return true
    } catch (cause) {
      if (accepts(guildId, requestGeneration)) error.value = errorMessage(cause)
      return false
    } finally {
      finishMutation(guildId, 'timers', requestGeneration)
    }
  }

  async function modify(
    guildId: string,
    timerId: string,
    input: ModifyGuildNotificationTimerInput,
  ): Promise<boolean> {
    return mutateTimer<ModifyGuildNotificationTimerMutation>(
      guildId,
      ModifyGuildNotificationTimerDocument,
      { guildId, timerId, name: input.name, gameTimer: input.gameTimer },
      (data) => data.modifyGuildNotificationTimer,
    )
  }

  async function start(
    guildId: string,
    timerId: string,
    gameTimer: string,
  ): Promise<boolean> {
    return mutateTimer<StartGuildNotificationTimerMutation>(
      guildId,
      StartGuildNotificationTimerDocument,
      { guildId, timerId, gameTimer },
      (data) => data.startGuildNotificationTimer,
    )
  }

  async function stop(guildId: string, timerId: string): Promise<boolean> {
    return mutateTimer<StopGuildNotificationTimerMutation>(
      guildId,
      StopGuildNotificationTimerDocument,
      { guildId, timerId },
      (data) => data.stopGuildNotificationTimer,
    )
  }

  async function remove(guildId: string, timerId: string): Promise<boolean> {
    const requestGeneration = generation
    const requestLiveRevision = liveRevision
    advanceMutationRevision(guildId, 'timers')
    error.value = null
    try {
      const result = await apolloClient.mutate<RemoveGuildNotificationTimerMutation>({
        mutation: RemoveGuildNotificationTimerDocument,
        variables: { guildId, timerId },
      })
      if (!accepts(guildId, requestGeneration)) return false
      const removal = result.data?.removeGuildNotificationTimer
      if (removal === undefined || removal === null) {
        throw new Error('wwm-notify did not return the removed timer.')
      }
      requireGuild(guildId, removal.guildId)
      if (removal.timerId !== timerId) {
        throw new Error('wwm-notify removed a different timer.')
      }
      if (requestLiveRevision === liveRevision) {
        timers.value = timers.value.filter((timer) => timer.id !== removal.timerId)
      }
      error.value = null
      return true
    } catch (cause) {
      if (accepts(guildId, requestGeneration)) error.value = errorMessage(cause)
      return false
    } finally {
      finishMutation(guildId, 'timers', requestGeneration)
    }
  }

  async function mutateTimer<T>(
    guildId: string,
    document: object,
    variables: Record<string, string | null>,
    select: (data: T) => GuildNotificationTimerWire,
  ): Promise<boolean> {
    const requestGeneration = generation
    const requestLiveRevision = liveRevision
    advanceMutationRevision(guildId, 'timers')
    error.value = null
    try {
      const result = await apolloClient.mutate<T>({ mutation: document, variables })
      if (!accepts(guildId, requestGeneration)) return false
      if (result.data === undefined || result.data === null) {
        throw new Error('wwm-notify did not return the updated timer.')
      }
      if (requestLiveRevision === liveRevision) replaceTimer(mapTimer(select(result.data)))
      error.value = null
      return true
    } catch (cause) {
      if (accepts(guildId, requestGeneration)) error.value = errorMessage(cause)
      return false
    } finally {
      finishMutation(guildId, 'timers', requestGeneration)
    }
  }

  async function setStatusChannel(
    guildId: string,
    channelId: string | null,
  ): Promise<boolean> {
    const requestGeneration = generation
    const requestLiveRevision = liveRevision
    advanceMutationRevision(guildId, 'statusChannel')
    error.value = null
    try {
      const result = await apolloClient.mutate<SetGuildNotificationTimerStatusChannelMutation>({
        mutation: SetGuildNotificationTimerStatusChannelDocument,
        variables: { guildId, channelId },
      })
      if (!accepts(guildId, requestGeneration)) return false
      const channel = result.data?.setGuildNotificationTimerStatusChannel
      if (channel === undefined || channel === null) {
        throw new Error('wwm-notify did not return the status channel.')
      }
      requireGuild(guildId, channel.guildId)
      if (requestLiveRevision === liveRevision) statusChannel.value = mapStatusChannel(channel)
      error.value = null
      return true
    } catch (cause) {
      if (accepts(guildId, requestGeneration)) error.value = errorMessage(cause)
      return false
    } finally {
      finishMutation(guildId, 'statusChannel', requestGeneration)
    }
  }

  return {
    timers,
    statusChannel,
    loading,
    error,
    load,
    add,
    modify,
    start,
    stop,
    remove,
    setStatusChannel,
    prepareGuild,
    applyLiveState,
    markUnavailable,
    reportLiveError,
  }
}
